import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import ControlPanelSidebar from "../components/control-panel/ControlPanelSidebar";
import {
    NodeState,
    selectSelectedCell,
    selectSelectedTool,
    setControlPanelStatus,
    setSelectedAgencyToolIndex,
    setSelectedCellId,
    setSelectedControlPanelComponent,
    setSelectedToolIdentifier
} from "../store/slices/node.slice";
import { ILGraphNode } from "../types/litegraph-update";
import { cellsSetupTools } from "../utils/cell-setup";
import useGlobalVariables from "../utils/global-variables";

const RigidToolDisplayNodeView = () => {
    const dispatch = useDispatch();
    const { editor, LiteGraph, LGraphGroup } = useGlobalVariables();
    const location = useLocation();
    const navigate = useNavigate();
    const selectedCell = useSelector(selectSelectedCell);
    const selectedTool = useSelector(selectSelectedTool);
    const toolsNodeList = useRef<ILGraphNode[]>([]);
    const [graphLoaded, setGraphLoaded] = useState(false);
    const selectedCellRef = useRef(selectedCell);
    const selectedToolRef = useRef(selectedTool);
    const nodesDataState: NodeState = useSelector((state: any) => state.nodeData);
    const isRigidCell = nodesDataState.collabApp?.type === "rigid";

    useEffect(() => {
        selectedCellRef.current = selectedCell;
        selectedToolRef.current = selectedTool;
    }, [selectedCell, selectedTool]);

    useEffect(() => {
        if (LiteGraph) {
            cellsSetupTools(LiteGraph);
        }
    }, [LiteGraph]);

    useEffect(() => {
        if (editor && !graphLoaded) {
            // Clear graph
            editor.graphcanvas.graph.clear();

            // On Node Add
            editor.graphcanvas.graph.onNodeAdded = function (node: ILGraphNode) {
                if (node.type === "Main/AgencyToolNode") {
                    // Connect to previous tool
                    toolsNodeList.current.at(-1)?.connect(0, node, 0);

                    toolsNodeList.current.push(node);

                    node.onDblClick = (e) => {
                        e.preventDefault();
                        dispatch(setSelectedAgencyToolIndex(node.properties?.identifier));
                        /**
                         * Open sidebar
                         */
                        dispatch(setControlPanelStatus("warning"));
                        dispatch(setSelectedControlPanelComponent("agencyTools"));
                    };
                }
            };

            setGraphLoaded(true);
        }

        // Set cell Id
        if (location.search) {
            const urlParams = new URLSearchParams(location.search);
            const cellId = nodesDataState.cells?.[0]?.unique_identifier;
            const displayedTool = urlParams.get("displayed_tool");
            if (cellId && displayedTool) {
                // set selected cell in state
                dispatch(setSelectedCellId(cellId));
                // Set selected tool identifier
                dispatch(setSelectedToolIdentifier(parseInt(displayedTool)));
            }
        }
    }, [editor, location.search]);

    useEffect(() => {
        /**
         * In the case of rigid collab app group, create a group with perceptor input and activator endpoint
         * Create custom nodes for rigid type tools
         * Get the corresponding arguments of the display tools from the cell[0].tool_wishlist[0].selected_tool
         * When the arguments are updates, update the correspond `default_value` of the arguments of the selected_tool.
         *
         * If the tool type is not rigid, construct the nodes with the tool_wishlist
         */
        if (LiteGraph && editor) {
            console.log("Initialized");
            if (nodesDataState.collabApp?.type === "rigid") {
                const group = new LGraphGroup();

                let nodeSize = [0, 0],
                    toolStartingPositionX = 700,
                    toolStartingPositionY = 250;
                const startingPositionX = 250,
                    startingPosisionY = 150,
                    agencyToolWidth = 300,
                    agencyToolHeight = 45,
                    displayNodes = selectedToolRef.current?.display?.nodes || [],
                    // cellTools = nodesDataState.cells?.[0]?.tool_wishlist || [],
                    toolsXOffset = 50,
                    toolsYOffset = 50;

                /**
                 * Make the height of the group be equal to;
                 * = height of top padding + node height + toolsYOffset + plus bottom padding
                 */
                group.pos = [startingPositionX + 400, startingPosisionY - 10];
                group.size = [
                    50 + (agencyToolWidth + toolsXOffset) * displayNodes.length,
                    100 + (agencyToolHeight + toolsYOffset) * displayNodes.length + 50
                ]; // Set the size of the group
                group.title =
                    (selectedToolRef.current?.title ||
                        selectedToolRef.current?.selected_tool.display_name ||
                        selectedToolRef.current?.selected_tool.tool_name) + " Flow";
                group.removable = false;

                editor?.graph.add(group);

                if (displayNodes.length > 0) {
                    for (const [index, tool] of displayNodes.entries()) {
                        const toolsNode = LiteGraph.createNode("Main/AgencyToolNode");
                        toolsNode.pos = [toolStartingPositionX, toolStartingPositionY];

                        toolsNode.title = tool.title;
                        toolsNode.inputs[0].label =
                            tool.description.length > 45
                                ? tool.description.slice(0, 43) + ".."
                                : tool.description;

                        toolsNode.properties = {
                            identifier: index
                        };

                        // toolsNode.removable = false;
                        editor?.graph.add(toolsNode);

                        nodeSize = toolsNode.size;

                        toolStartingPositionX += nodeSize[0] + toolsXOffset;
                        toolStartingPositionY += nodeSize[1] + toolsYOffset;
                    }
                }
            }
        }
    }, [editor, nodesDataState.collabApp, dispatch]);

    const navigateToTools = () => {
        // close side nav
        dispatch(setControlPanelStatus("hidden"));
        // Navigate to home page
        navigate(`/capabilities?${location.search}`);
    };

    return (
        <main>
            <ControlPanelSidebar
                breadcrumbs={[
                    {
                        label: isRigidCell ? "Rigid Cell" : "",
                        onClick: isRigidCell ? undefined : navigateToTools,
                        isCurrentPage: false
                    },
                    {
                        label:
                            selectedToolRef.current?.title ||
                            selectedToolRef.current?.selected_tool.display_name ||
                            selectedToolRef.current?.selected_tool.tool_name ||
                            "",
                        isCurrentPage: true
                    }
                ]}
            />
        </main>
    );
};

export default RigidToolDisplayNodeView;
